/**
 * Tipo y etiquetas de los `fin_documents.kind` que se guardan en BD. La
 * lista debe mantenerse en sync con el CHECK en schema.ts.
 *
 * A nivel UI las facturas se subdividen en PDF/XML para que el usuario
 * pueda marcar explícitamente cuál sube (CFDI = PDF + XML por separado).
 * La distinción es por content_type del archivo, no por una columna nueva
 * — el backend sigue viendo 5 kinds.
 */
export type DocKind =
  | "edo_cuenta"
  | "factura_emitida"
  | "factura_recibida"
  | "invoice"
  | "declaracion"
  | "otro";

export type DocKindUi =
  | "edo_cuenta"
  | "factura_emitida_pdf"
  | "factura_emitida_xml"
  | "factura_recibida_pdf"
  | "factura_recibida_xml"
  | "invoice"
  | "declaracion"
  | "otro";

export const DOC_KIND_UI_LABEL: Record<DocKindUi, string> = {
  edo_cuenta: "Estado de cuenta",
  factura_emitida_pdf: "Factura emitida (PDF)",
  factura_emitida_xml: "Factura emitida (XML)",
  factura_recibida_pdf: "Factura recibida (PDF)",
  factura_recibida_xml: "Factura recibida (XML)",
  invoice: "Invoice (extranjero)",
  declaracion: "Declaración SAT",
  otro: "Otro",
};

export const DOC_KINDS_UI = Object.keys(DOC_KIND_UI_LABEL) as DocKindUi[];

/**
 * Etiquetas de `DocKind` (forma backend). Útiles cuando ya tenemos un
 * documento subido y solo conocemos su kind (sin content_type para
 * distinguir PDF/XML).
 */
export const DOC_KIND_LABEL: Record<DocKind, string> = {
  edo_cuenta: "Estado de cuenta",
  factura_emitida: "Factura emitida",
  factura_recibida: "Factura recibida",
  invoice: "Invoice (extranjero)",
  declaracion: "Declaración SAT",
  otro: "Otro",
};

export const DOC_KINDS = Object.keys(DOC_KIND_LABEL) as DocKind[];

/**
 * Mapea el kind de UI al kind que se envía al backend. La distinción
 * PDF/XML viaja por content_type al subir el archivo.
 */
export function uiKindToKind(ui: DocKindUi): DocKind {
  if (ui === "factura_emitida_pdf" || ui === "factura_emitida_xml") return "factura_emitida";
  if (ui === "factura_recibida_pdf" || ui === "factura_recibida_xml") return "factura_recibida";
  return ui;
}

/**
 * Mapea kind + content_type al kind de UI (para listar documentos con la
 * granularidad PDF/XML correcta).
 */
export function uiKindOf(kind: DocKind, contentType: string | null | undefined): DocKindUi {
  const isXml = !!contentType && /xml/i.test(contentType);
  if (kind === "factura_emitida") return isXml ? "factura_emitida_xml" : "factura_emitida_pdf";
  if (kind === "factura_recibida") return isXml ? "factura_recibida_xml" : "factura_recibida_pdf";
  return kind;
}

/**
 * Heurística para sugerir el UI kind a partir del nombre o tipo MIME del
 * archivo. Pone PDF vs XML según la extensión.
 */
export function guessDocKindUi(file: File, fallback: DocKindUi = "otro"): DocKindUi {
  const name = file.name.toLowerCase();
  const isXml =
    name.endsWith(".xml") || file.type === "text/xml" || file.type === "application/xml";

  if (/(edo|estado).*cuenta|state[_-]?ment/.test(name)) return "edo_cuenta";
  if (/declar/.test(name)) return "declaracion";

  // factura/cfdi → CFDI mexicano (recibida o emitida según fallback);
  // "invoice" (literal) → invoice extranjero (sin CFDI mx).
  if (/^invoice|[_-]invoice/.test(name) && !/factura|cfdi/.test(name)) {
    return "invoice";
  }
  if (/factura|cfdi|invoice/.test(name) || isXml) {
    if (fallback === "factura_emitida_pdf" || fallback === "factura_emitida_xml") {
      return isXml ? "factura_emitida_xml" : "factura_emitida_pdf";
    }
    return isXml ? "factura_recibida_xml" : "factura_recibida_pdf";
  }
  // Si el fallback ya es una factura, mantener su lado emitida/recibida pero
  // ajustar a PDF/XML según el archivo.
  if (fallback.startsWith("factura_emitida")) {
    return isXml ? "factura_emitida_xml" : "factura_emitida_pdf";
  }
  if (fallback.startsWith("factura_recibida")) {
    return isXml ? "factura_recibida_xml" : "factura_recibida_pdf";
  }
  return fallback;
}
